import { useCallback, useEffect, useMemo } from 'react';
import {
  useAuth,
  useClerk,
  useUser as useClerkUser,
} from '@clerk/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/apis/client';
import getUserApi from '@/apis/get-user.api';
import QUERY_KEYS from '@/constants/query-keys';
import { User, UserWithMandatoryPicture } from '@/types/user';

export default function useUser() {
  const { signOut, openSignIn, isSignedIn: isClerkSignedIn } = useClerk();
  const { getToken, isLoaded: isClerkLoaded } = useAuth();
  const { user: clerkUser, isLoaded: isUserLoaded } = useClerkUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const {
    isLoading: isUserQueryLoading,
    data: userData,
    error: userQueryError,
  } = useQuery({
    queryKey: QUERY_KEYS.USER,
    enabled: !!isClerkSignedIn && isClerkLoaded && isUserLoaded,
    queryFn: async (): Promise<User | undefined> => {
      const token = await getToken();
      if (token) {
        return getUserApi(token);
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (
        ApiError.isApiError(error) &&
        (error.status === 401 || error.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const user = useMemo<UserWithMandatoryPicture | undefined>(() => {
    if (!userData || !clerkUser) return undefined;
    return {
      ...userData,
      picture: clerkUser.imageUrl,
    };
  }, [clerkUser, userData]);

  const logoff = useCallback(async (): Promise<void> => {
    try {
      await signOut();
    } finally {
      // Clear cache anyway
      queryClient.clear();
    }
  }, [queryClient, signOut]);

  const login = useCallback(
    (to?: string): void => {
      openSignIn({
        oauthFlow: 'popup',
        signUpForceRedirectUrl: to ?? null,
        forceRedirectUrl: to ?? null,
      });
    },
    [openSignIn]
  );

  const _getToken = useCallback(async (): Promise<string> => {
    if (!isClerkSignedIn) {
      throw new Error('User not signed in');
    }

    const token = await getToken();
    if (!token) {
      await logoff();
      throw new Error('No token available');
    }
    return token;
  }, [getToken, logoff, isClerkSignedIn]);

  useEffect(() => {
    if (userQueryError) {
      // Handle different types of errors appropriately
      if (ApiError.isApiError(userQueryError)) {
        if (userQueryError.status === 401 || userQueryError.status === 403) {
          // Authentication/authorization errors - log out user
          toast.error(t('use_user.error.auth'));
          logoff();
          return;
        } else if (userQueryError.status >= 500) {
          // Server errors - show generic error but don't log out
          toast.error(t('use_user.error.server'));
          return;
        }
      }

      // Network or other errors
      toast.error(t('use_user.error.fetch'));
    }
  }, [userQueryError, logoff, t]);

  return {
    logoff,
    login,
    user,
    isSignedIn: isClerkSignedIn && !!user,
    getToken: _getToken,
    isPendingUser:
      !isClerkLoaded ||
      !isUserLoaded ||
      (isClerkSignedIn && isUserQueryLoading),
  };
}
